import cx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Button from '@components/uikit/Button/Button';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import ModalSteps from '../ModalSteps/ModalSteps';
import AiTypeIcon from './components/AiTypeIcon/AiTypeIcon';
import { aiTypesList } from './constants/aiTypesList';
import { ADD_AI_FIELDS, getAddAiDefaultValues } from './constants/schemaAddActionItem';
import { checkActiveAI } from './helpers/checkActiveAI';
import { getAddAiSteps } from './helpers/getAddAiSteps';
import { getStepName } from './helpers/getStepName';
import { useActionItemCreateMutation } from './services/useActionItemCreateMutation';
import { useSevCreateMutation } from './services/useSevCreateMutation';
import style from './style.module.scss';

const AddActionItemModal = ({ filledData }) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setModalClose } = useModalActions();

  const { data: profileData } = useUserProfileQuery();

  const [step, setStep] = useState(filledData?.currentStep || 0);

  const stepsList = getAddAiSteps();
  const currentStep = stepsList[step];

  const CurrentStepComponent = currentStep?.stepComponent;

  const progressBarSteps = stepsList.slice(0, stepsList.length);

  const { control, handleSubmit, reset, trigger, watch, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: currentStep?.schema,
    defaultValues: getAddAiDefaultValues(filledData),
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const [aiOptionValue, aiTypeValue, equipmentType, instPointValue] = watch([
    ADD_AI_FIELDS.OPTION,
    ADD_AI_FIELDS.TYPE,
    ADD_AI_FIELDS.EQUIPMENT_TYPE,
    ADD_AI_FIELDS.INSTALLATION_POINT,
  ]);

  const stepName = getStepName(t, currentStep.step, aiOptionValue);
  const hasActiveAI = !!checkActiveAI(instPointValue, aiTypeValue);
  const typeCardInfo = aiTypesList.find((type) => type.name === aiTypeValue);

  const onSuccess = (data) => {
    if (data?.status === 'error') {
      showToast({
        variant: 'error',
        message: data?.message || t('Something went wrong! Please, try again later.'),
      });
    } else {
      handleClose();
      showToast({ variant: 'success', message: t('Report has been added.') });
    }
  };

  const { mutate: actionItemCreateMutate, isLoading: actionItemCreateLoading } = useActionItemCreateMutation({
    onSuccess,
  });

  const { mutate: sevCreateMutate, isLoading: sevCreateLoading } = useSevCreateMutation({ onSuccess });

  const submit = handleSubmit((data) => {
    const formData = {
      description: data.description || '',
      urgency: +data.urgency,
      type: aiTypeValue,
    };

    if (aiOptionValue === 'reportFailure') {
      formData['customer_id'] = customerId;
      formData['facility_id'] = data.installation_point?.facilityId;
      formData['user_id'] = profileData?.user.id;
      formData['approximate_date'] = formatInTimeZone(
        new Date(data.approximate_date),
        'UTC',
        'yyyy/MM/dd HH:mm',
      );
    }

    if (aiTypeValue === 'regular') {
      formData['installation_point_id'] = data.installation_point?.id;
    } else {
      formData['equipment_type'] = data.equipment_type.id;

      if (['gateway', 'router', 'node'].includes(equipmentType.id)) {
        formData['serial'] = data.installation_point.serial;
      } else {
        formData['installation_point_id'] = data.installation_point.id;
      }
    }

    if (aiOptionValue === 'reportFailure') {
      sevCreateMutate(formData);
    } else {
      actionItemCreateMutate(formData);
    }
  });

  const handleOpenActiveAi = () => {
    navigate(`/customer/${customerId}/ai/${checkActiveAI(instPointValue, aiTypeValue)}`, {
      state: { prevPage: pathname },
    });
    handleClose();
  };

  const handleNextStep = async () => {
    const nextStep = currentStep.nextStepIndex;
    if (nextStep === 'end') {
      submit();
      return;
    }
    const isValid = await trigger();
    if (isValid) {
      setStep(nextStep);
    }
  };

  const handleBack = () => {
    const previousStep = currentStep.previousStepIndex;

    if (previousStep === 1 && !filledData) {
      const defaultValues = getAddAiDefaultValues();

      reset({
        ...defaultValues,
        [ADD_AI_FIELDS.OPTION]: aiOptionValue,
        [ADD_AI_FIELDS.TYPE]: aiTypeValue,
      });
    }

    if (previousStep === 'close') {
      handleClose();
      return;
    }

    setStep(previousStep);
  };

  return (
    <div className={style.createAiModal}>
      <ModalHeader title={'Add action item'} variant={'withoutSeparator'} onClose={handleClose} />
      <ModalSteps stepName={stepName} currentStep={currentStep?.step} steps={progressBarSteps} />
      <div className={cx(style.modalBody, { [style.modalBodyForm]: currentStep.showStepIcon })}>
        {currentStep.showStepIcon && (
          <div className={style.cardItem}>
            <div className={style.cardItemIconWrapper}>
              <AiTypeIcon iconKey={typeCardInfo.icon[aiOptionValue]} />
            </div>
            <p className={style.cardItemTitle}>{t(typeCardInfo.cardTitle[aiOptionValue])}</p>
            <p className={style.cardItemDescription}>{t(typeCardInfo.description[aiOptionValue])}</p>
          </div>
        )}
        <CurrentStepComponent
          control={control}
          resetField={resetField}
          setValue={setValue}
          isFilledPoint={!!filledData?.installationPoint}
          filledAiType={filledData?.aiType}
        />
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Back')} onClick={handleBack} />
        {hasActiveAI ? (
          <Button
            type="primary"
            variant={'medium'}
            text={t('Go to Action Item')}
            onClick={handleOpenActiveAi}
          />
        ) : (
          <Button
            type="primary"
            variant={'medium'}
            text={t(currentStep?.nextStepIndex === 'end' ? 'Submit' : 'Next')}
            disabled={actionItemCreateLoading || sevCreateLoading}
            onClick={handleNextStep}
          />
        )}
      </div>
    </div>
  );
};

export default AddActionItemModal;
